/**
 * SubscriptionDetailsModal - Modal premium de detalhes da Assinatura (Recorrência)
 *
 * Estrutura: cabeçalho (status + KPIs) → pagador → composição (itens snapshot)
 * → frequência → tabela de ciclos com ações:
 *   - Antecipar emissão (apenas ciclos aguardando_emissao)
 *   - Pausar / Despausar
 *   - Cancelar Recorrência (com opção de cancelar no provider)
 *
 * Toda leitura via RPC get_assinatura_details (hook useAssinaturaDetalhes).
 *
 * @author Módulo Financeiro - Recorrência
 * @version 1.8.2
 * RELEASE: 2026-10-19 v1.8.2 - Causa raiz corrigida na ScrollArea v1.8.0 (wheel do modal roubava a rolagem da lista).
 * RELEASE: 2026-10-19 v1.8.1 - Lista de ciclos volta a rolar: altura fixa 27rem quando há mais de 8 ciclos
 *          e sem overscroll-contain, para a rolagem passar ao modal ao chegar no fim da lista.
 * RELEASE: 2026-10-19 v1.8.0 - Seção Ciclos com rolagem isolada igual à timeline do ChargeDetailsModal:
 *          div nativo (max-h 27rem + overflow-y-auto + overscroll-contain) no lugar da ScrollArea interna,
 *          impedindo que a rolagem da lista "vaze" para a barra externa do modal.
 * RELEASE: 2026-09-18 v1.7.0 - Corrigida a rolagem da seção Ciclos: a lista usava div com max-h/overflow
 *          aninhada na ScrollArea externa do modal, cujo contêiner em layout de tabela anulava a altura
 *          máxima e impedia rolar além dos primeiros ciclos. A lista passa a usar ScrollArea própria com
 *          altura fixa (27rem) e o cabeçalho das colunas fica fixo fora da área rolável.
 * RELEASE: 2026-06-09 v1.0.0 - Criação inicial (Fase 5 do módulo Recorrência)
 * RELEASE: 2026-06-09 v1.1.0 - Layout alinhado ao ChargeDetailsModal (KPIs, Composição e Ciclos)
 * RELEASE: 2026-06-16 v1.2.1 - Invalida cache ['assinatura-detalhes', id] ao fechar ChargeDetailsModal aninhado, garantindo refresh automático dos ciclos após Reenviar Provider, Editar ou Cancelar.
 * RELEASE: 2026-06-21 v1.3.0 - Link da coluna LINK em cada ciclo passa por resolveCheckoutUrl, respeitando checkout_mode da conta corrente (transparente x provider) e usando checkout_session_slug exposto pela RPC get_assinatura_details v1.6.0. Aplicado tanto na tabela desktop quanto no card mobile.
 * RELEASE: 2026-06-22 v1.4.0 - Adicionado atalho (ícone) na seção Pagador para abrir o CustomerDetailsModalPremium do cliente vinculado.
 * RELEASE: 2026-06-27 v1.5.0 - Cada ciclo passa receipt_url (RPC v1.9.0) para o ChargeProviderActions, exibindo o ícone "Recibo" na coluna Ações quando o comprovante de pagamento está disponível (paridade total com ChargeDetailsModal v7.3.0).
 * RELEASE: 2026-08-19 v1.6.0 - Coluna Ações da tabela de Ciclos (desktop e mobile) ganha o botão "Excluir ciclo",
 *          disponível apenas para status canceláveis (aguardando_emissao, pendente, aguardando_pagamento, vencido,
 *          parcial) e para usuários com a permissão `financial_charges.delete`. Reaproveita a RPC validada
 *          `cancel_charge_manual` (via hook useCancelChargeManual), que cancela a cobrança + parcelas de forma
 *          atômica e enfileira o cancelamento no provider quando a cobrança já foi emitida. Ciclos pagos,
 *          estornados, cancelados ou em `processando_cancelamento` não exibem o botão.
 */
import { FinanceiroVehiclesBlock } from '@/components/financial/FinanceiroVehiclesBlock';
import { useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Repeat,
  User,
  CreditCard,
  Calendar,
  Zap,
  PauseCircle,
  PlayCircle,
  XCircle,
  ExternalLink,
  Receipt,
  AlertTriangle,
  Building2,
  Landmark,

  Loader2,
  Tag,
  ShoppingCart,
  DollarSign,
  CheckCircle2,
  Package,
  Rocket,
  Pencil,
  History,
  ChevronDown,
  FileText,
  ArrowUpRight,
  // v1.6.0 — ícone da ação "Excluir ciclo" (cancelamento da cobrança gerada)
  Trash2,
} from 'lucide-react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { parseDateLocal } from '@/lib/utils';
import { formatDocumentForDisplay } from '@/lib/input-masks';
import { formatPhoneForDisplay } from '@/lib/phone-utils';

import { useAssinaturaDetalhes } from '@/hooks/useAssinaturaDetalhes';
import { useAssinaturaActions } from '@/hooks/useAssinaturaActions';
import { useSavedCards } from '@/hooks/financial/useSavedCards';
import { SavedCardsList } from '@/components/financial/SavedCardsList';
import { TIPOS_PAGAMENTO } from '@/hooks/useFinanceiroMeiosPagamento';
import { PaymentMethodIcon, DefaultPaymentIcon } from '@/components/icons/PaymentMethodIcons';
import { EditSubscriptionModal } from './EditSubscriptionModal';
import { ChargeProviderActions } from './ChargeProviderActions';
// v1.2.0 — abrir o modal de detalhes da cobrança ao clicar nos campos do ciclo
import { ChargeDetailsModal } from './ChargeDetailsModal';
import { ChargeTimeline } from './ChargeTimeline';
import { InlineNotice } from '@/components/ui/inline-notice';
import { AnticipateConfirmDialog } from './AnticipateConfirmDialog';
import { usePermissions } from '@/hooks/usePermissions';
import type { FinanceiroCobranca } from '@/hooks/useFinanceiroCobrancas';
import { SubscriptionJurosSection } from '@/components/financial/subscription/SubscriptionJurosSection';
import { SubscriptionExpiracaoSection } from '@/components/financial/subscription/SubscriptionExpiracaoSection';
// v1.3.0 — resolve link de pagamento por ciclo respeitando checkout_mode da conta corrente
import { resolveCheckoutUrl } from '@/lib/financial/resolveCheckoutUrl';
// v1.2.1 — invalidação manual do cache da recorrência ao fechar modais aninhados
import { useQueryClient } from '@tanstack/react-query';
// v1.4.0 — modal de detalhes do cliente acionado pelo atalho na seção Pagador
import { CustomerDetailsModalPremium } from '@/components/crm/customers/modal/CustomerDetailsModalPremium';
// v1.6.0 — exclusão (cancelamento) de um ciclo gerado direto na listagem de Ciclos
import { useCancelChargeManual } from '@/hooks/useCancelChargeManual';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';



interface SubscriptionDetailsModalProps {
  assinaturaId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Mapa de status (badges principais) */
const statusBadge: Record<string, { label: string; className: string }> = {
  ativa: { label: 'Ativa', className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' },
  pausada: { label: 'Pausada', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' },
  cancelada: { label: 'Cancelada', className: 'bg-gray-200 text-gray-800 dark:bg-gray-800 dark:text-gray-200' },
  finalizada: { label: 'Finalizada', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
};

/** Mapa de status dos ciclos */
const cicloStatusBadge: Record<string, { label: string; className: string; bar: string }> = {
  aguardando_emissao: {
    label: 'Aguardando emissão',
    className: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
    bar: 'bg-slate-400',
  },
  pendente: {
    label: 'Pendente',
    className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
    bar: 'bg-yellow-500',
  },
  edicao_andamento: {
    label: 'Edição em andamento',
    className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
    bar: 'bg-amber-500',
  },
  aguardando_pagamento: {
    label: 'Aguardando Pagamento',
    className: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
    bar: 'bg-orange-500',
  },
  pago: {
    label: 'Pago',
    className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    bar: 'bg-green-500',
  },
  vencido: {
    label: 'Vencido',
    className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
    bar: 'bg-red-500',
  },
  cancelado: {
    label: 'Cancelado',
    className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
    bar: 'bg-red-500',
  },
};

const FREQ_LABEL: Record<string, string> = {
  semanal: 'Semanal',
  quinzenal: 'Quinzenal',
  mensal: 'Mensal',
  bimestral: 'Bimestral',
  trimestral: 'Trimestral',
  semestral: 'Semestral',
  anual: 'Anual',
  bienal: 'Bienal',
};

const formatBRL = (v: number, moeda = 'BRL') =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: moeda || 'BRL' }).format(v || 0);

export function SubscriptionDetailsModal({
  assinaturaId,
  open,
  onOpenChange,
}: SubscriptionDetailsModalProps) {
  const { data, isLoading } = useAssinaturaDetalhes(assinaturaId, open);
  const { anteciparCiclo, pausar, cancelar, atualizar, isBusy } = useAssinaturaActions(assinaturaId);
  // Feature 2 — permissão dedicada para o botão "Antecipar" (debita cartão imediatamente)
  const { canCreate, canDelete } = usePermissions();
  const canAnticipate = canCreate('financial_subscription_anticipate');
  // v1.6.0 — mesma permissão exigida pela RPC cancel_charge_manual no backend
  const canDeleteCharge = canDelete('financial_charges');
  const cancelChargeManual = useCancelChargeManual();
  // Fase 5: troca de cartão padrão
  const [changeCardOpen, setChangeCardOpen] = useState(false);
  const [pickedCardId, setPickedCardId] = useState<string | null>(null);
  // v1.2.1 — usado para invalidar manualmente o cache ao fechar modais aninhados
  const queryClient = useQueryClient();

  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelarNoProvider, setCancelarNoProvider] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  // v1.2.0 — estado do modal aninhado de detalhes da cobrança (ciclo clicado)
  const [chargeDetailsOpen, setChargeDetailsOpen] = useState(false);
  const [selectedCicloCobrancaId, setSelectedCicloCobrancaId] = useState<string | null>(null);
  // Feature 2 — armazena o ciclo completo (não só o id) para enriquecer o
  // diálogo de confirmação com valor, vencimento, cliente e cartão.
  const [cicloAntecipar, setCicloAntecipar] = useState<{
    cobranca_id: string;
    ciclo_numero: number;
    valor_total: number;
    data_vencimento: string;
  } | null>(null);
  // v1.6.0 — ciclo selecionado para exclusão (cancelamento da cobrança gerada) + motivo opcional
  const [cicloExcluir, setCicloExcluir] = useState<{
    cobranca_id: string;
    ciclo_numero: number;
    valor_total: number;
    data_vencimento: string;
    tem_provider: boolean;
    
  } | null>(null);
  const [motivoExclusao, setMotivoExclusao] = useState('');
  // Sanfona das seções de Ciclos e Histórico (evita estourar a tela)
  const [ciclosOpen, setCiclosOpen] = useState(true);
  const [historicoOpen, setHistoricoOpen] = useState(false);
  // v1.2.1 — Permissão de edição/exclusão da cobrança agora é resolvida internamente
  // pelo ChargeDetailsModal (via usePermissions('financial_charges')), garantindo o
  // mesmo conjunto de botões do rodapé quando aberto a partir da página Cobranças.
  // v1.4.0 — controla abertura do CustomerDetailsModalPremium via atalho na seção Pagador
  const [customerModalOpen, setCustomerModalOpen] = useState(false);

  /** Abre o modal de detalhes da cobrança para o ciclo selecionado */
  const openCicloDetails = (cobrancaId: string) => {
    setSelectedCicloCobrancaId(cobrancaId);
    setChargeDetailsOpen(true);
  };

  const assin = data?.assinatura;
  const customer = data?.customer;
  const meio = data?.meio_pagamento;
  const conta = data?.conta_corrente;
  const itens = data?.itens || [];
  const ciclos = data?.ciclos || [];

  // Mapas UUID → nome para resolver meio_pagamento_id / conta_corrente_id no diff da timeline.
  // Combina ciclos (histórico) + entidades atuais. UUIDs não encontrados caem em fallback 8-char.
  const timelineLookups = useMemo(() => {
    const meio_pagamento: Record<string, string> = {};
    const conta_corrente: Record<string, string> = {};
    for (const c of ciclos) {
      if (c.meio_pagamento_id && c.meio_pagamento_nome) {
        meio_pagamento[c.meio_pagamento_id] = c.meio_pagamento_nome;
      }
    }
    if (meio?.id && meio?.nome) meio_pagamento[meio.id] = meio.nome;
    if (conta?.id && conta?.nome) conta_corrente[conta.id] = conta.nome;
    return { meio_pagamento, conta_corrente };
  }, [ciclos, meio, conta]);


  // v1.3.0 — modo de checkout (transparente x provider) preferido pela conta corrente
  const checkoutMode = (conta?.checkout_mode ?? 'transparent') as 'transparent' | 'provider';

  const status = assin?.status || 'ativa';
  const stBadge = statusBadge[status] || statusBadge.ativa;
  const isAtiva = status === 'ativa';
  const isPausada = status === 'pausada';
  const isCancelada = status === 'cancelada' || status === 'finalizada';

  // ===== KPIs locais (a partir da lista de ciclos) =====
  const ciclosPagos = ciclos.filter(c => c.status === 'pago').length;
  const ciclosPendentes = ciclos.filter(c =>
    ['aguardando_emissao', 'pendente', 'aguardando_pagamento'].includes(c.status),
  ).length;
  const totalRecebido = ciclos
    .filter(c => c.status === 'pago')
    .reduce((sum, c) => sum + Number(c.valor_pago || c.valor_total || 0), 0);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className={cn(
            'p-0 gap-0 overflow-hidden',
            'sm:max-w-5xl sm:rounded-lg',
            'max-sm:fixed max-sm:inset-0 max-sm:translate-x-0 max-sm:translate-y-0 max-sm:w-full max-sm:h-[100dvh] max-sm:max-w-none max-sm:rounded-none',
            'flex flex-col h-full sm:h-[88vh]',
          )}
        >
          <DialogHeader className="px-5 py-4 border-b shrink-0">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                <div className="p-2 rounded-md bg-primary/10">
                  <Repeat className="h-5 w-5 text-primary" />
                </div>
                <div className="min-w-0">
                  <DialogTitle className="text-base flex items-center gap-2">
                    Recorrência
                    {assin && (
                      <span className="font-mono text-xs text-muted-foreground">
                        #{assin.id.substring(0, 8).toUpperCase()}
                      </span>
                    )}
                    <Badge className={cn('text-[11px]', stBadge.className)} variant="outline">
                      {stBadge.label}
                    </Badge>
                  </DialogTitle>
                  <DialogDescription className="text-xs truncate">
                    {assin?.descricao || customer?.name || 'Detalhes da assinatura'}
                  </DialogDescription>
                </div>
              </div>

              {/* Ações principais */}
              <div className="flex items-center gap-1.5 shrink-0 mr-8">
                {!isCancelada && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 gap-1.5"
                        onClick={() => setEditOpen(true)}
                        disabled={isBusy}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                        <span className="text-xs">Editar</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Editar recorrência (campos seguros e ciclos futuros)</TooltipContent>
                  </Tooltip>
                )}
                {!isCancelada && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 gap-1.5"
                        onClick={() => pausar.mutate(isAtiva)}
                        disabled={isBusy}
                      >
                        {pausar.isPending ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : isAtiva ? (
                          <PauseCircle className="h-3.5 w-3.5" />
                        ) : (
                          <PlayCircle className="h-3.5 w-3.5" />
                        )}
                        <span className="text-xs">{isAtiva ? 'Pausar' : 'Reativar'}</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {isAtiva
                        ? 'Pausa a geração de novos ciclos'
                        : 'Reativa a assinatura'}
                    </TooltipContent>
                  </Tooltip>
                )}
                {!isCancelada && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 gap-1.5 text-destructive hover:text-destructive"
                    onClick={() => setCancelOpen(true)}
                    disabled={isBusy}
                  >
                    <XCircle className="h-3.5 w-3.5" />
                    <span className="text-xs">Cancelar</span>
                  </Button>
                )}
              </div>
            </div>
          </DialogHeader>

          <ScrollArea className="flex-1 min-h-0">
            {isLoading || !assin ? (
              <div className="p-5 space-y-3">
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-40 w-full" />
              </div>
            ) : (
              <div className="p-5 space-y-6">
                {/*
                  v1.4.0 — Banner "cancelamento solicitado pelo cliente".
                  Lido de assin.metadata (gravado por tenant_subscription_cancel
                  v1.1.0). Informa ao operador master que o cliente pediu
                  encerramento e até quando o acesso permanece.
                */}
                {(() => {
                  const meta = (assin?.metadata ?? {}) as Record<string, unknown>;
                  const scheduledAt = meta.cancel_scheduled_at as string | undefined;
                  const effectiveAt = meta.cancel_effective_at as string | undefined;
                  const reason = (meta.cancel_reason as string | undefined) ?? null;
                  if (!scheduledAt && !effectiveAt) return null;
                  const eff = effectiveAt ? new Date(effectiveAt) : null;
                  const now = new Date();
                  const daysLeft = eff
                    ? Math.max(0, Math.ceil((eff.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)))
                    : null;
                  const fmt = (s: string) =>
                    new Date(s).toLocaleDateString('pt-BR');
                  return (
                    <InlineNotice variant={daysLeft && daysLeft > 0 ? 'warning' : 'info'}>
                      <div className="text-sm">
                        <strong className="block">
                          {daysLeft && daysLeft > 0
                            ? 'Cliente solicitou cancelamento da assinatura'
                            : 'Cancelamento efetivado'}
                        </strong>
                        {scheduledAt && (
                          <>Solicitado em <strong>{fmt(scheduledAt)}</strong>. </>
                        )}
                        {effectiveAt && daysLeft && daysLeft > 0 && (
                          <>Encerra em <strong>{daysLeft} dia{daysLeft === 1 ? '' : 's'}</strong>{' '}
                          (<strong>{fmt(effectiveAt)}</strong>). Nenhuma cobrança futura será gerada.</>
                        )}
                        {reason && (
                          <div className="mt-1 text-xs text-muted-foreground">
                            Motivo informado: {reason}
                          </div>
                        )}
                      </div>
                    </InlineNotice>
                  );
                })()}

                {/* ===== KPIs ===== */}
                <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">

                  {/* Valor do ciclo */}
                  <div className="bg-muted/30 border border-border/30 rounded-lg p-3 space-y-1.5">
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-3.5 w-3.5 text-primary/70" />
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                        Valor do ciclo
                      </span>
                    </div>
                    <p className="text-sm font-semibold text-foreground font-mono">{formatBRL(assin.valor_total)}</p>
                    <p className="text-[10px] text-muted-foreground">MRR estimado</p>
                  </div>
                  {/* Ciclos pagos */}
                  <div className="bg-muted/30 border border-border/30 rounded-lg p-3 space-y-1.5">
                    <div className="flex items-center gap-2">
                      <Receipt className="h-3.5 w-3.5 text-primary/70" />
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                        Ciclos pagos
                      </span>
                    </div>
                    <p className="text-sm font-semibold text-foreground">
                      {ciclosPagos}
                      <span className="text-xs text-muted-foreground ml-0.5">
                        /{assin.ciclos_total ?? '∞'}
                      </span>
                    </p>
                  </div>
                  {/* Pendentes */}
                  <div className="bg-muted/30 border border-border/30 rounded-lg p-3 space-y-1.5">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-3.5 w-3.5 text-primary/70" />
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                        Pendentes
                      </span>
                    </div>
                    <p className="text-sm font-semibold text-foreground">{ciclosPendentes}</p>
                  </div>
                  {/* Recebido */}
                  <div className="bg-muted/30 border border-border/30 rounded-lg p-3 space-y-1.5">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-3.5 w-3.5 text-primary/70" />
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                        Recebido
                      </span>
                    </div>
                    <p className="text-sm font-semibold text-foreground font-mono">{formatBRL(totalRecebido)}</p>
                  </div>
                </section>

                {/* ===== Pagador ===== */}
                <section className="space-y-3">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-primary" />
                    <h3 className="text-sm font-semibold">Pagador</h3>
                    {/* v1.4.0 — atalho para abrir o cadastro completo do cliente */}
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 ml-1"
                            disabled={!assin?.customer_id}
                            onClick={() => setCustomerModalOpen(true)}
                            aria-label="Abrir cadastro do cliente"
                          >
                            <ArrowUpRight className="h-3.5 w-3.5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Abrir cadastro do cliente</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                    <div>
                      <p className="text-[11px] text-muted-foreground">Nome</p>
                      <p className="font-medium truncate">{customer?.name || '—'}</p>
                    </div>
                    <div>
                      <p className="text-[11px] text-muted-foreground">Documento</p>
                      <p className="font-mono">
                        {customer?.cpf || customer?.cnpj
                          ? formatDocumentForDisplay(customer.cpf || customer.cnpj!)
                          : '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-[11px] text-muted-foreground">E-mail / Telefone</p>
                      <p className="text-xs truncate">
                        {customer?.email || (customer?.phone ? formatPhoneForDisplay(customer.phone) : '—')}
                      </p>
                    </div>

                  </div>
                </section>

                <Separator />

                {/* ===== Frequência / pagamento ===== */}
                <section className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-primary" />
                    <h3 className="text-sm font-semibold">Regra da recorrência</h3>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                    <div>
                      <p className="text-[11px] text-muted-foreground">Frequência</p>
                      <p className="font-medium">{FREQ_LABEL[assin.frequencia] || assin.frequencia}</p>
                    </div>
                    <div>
                      <p className="text-[11px] text-muted-foreground">Tipo de prazo</p>
                      <p className="font-medium capitalize">{assin.tipo_prazo}</p>
                    </div>
                    <div>
                      <p className="text-[11px] text-muted-foreground">Próximo vencimento</p>
                      <p className="font-medium">
                        {assin.proximo_vencimento
                          ? format(parseDateLocal(assin.proximo_vencimento), 'dd/MM/yyyy', { locale: ptBR })
                          : '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-[11px] text-muted-foreground">Dias antes da emissão</p>
                      <p className="font-medium">{assin.dias_antes_emissao}</p>
                    </div>
                  </div>

                  {/* Fase 5/C.2 — Cartão padrão da assinatura */}
                  {assin && assin.customer_id && (
                    <SubscriptionDefaultCardSection
                      assinaturaId={assin.id}
                      customerId={assin.customer_id}
                      defaultCardTokenId={(assin as any).default_card_token_id ?? null}
                      cardDunningState={(assin as any).card_dunning_state ?? null}
                      onOpenChange={setChangeCardOpen}
                      open={changeCardOpen}
                      pickedCardId={pickedCardId}
                      setPickedCardId={setPickedCardId}
                      atualizar={atualizar}
                    />
                  )}

                  {/* v1.3.0 — Pagamento & Conta em cards (padronizado com modal de detalhes da cobrança). */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                    {/* Coluna: Meio de Pagamento */}
                    {meio && (
                      <div className="bg-muted/30 border border-border/30 rounded-lg p-3 space-y-2">
                        <div className="flex items-center gap-2 mb-2">
                          <CreditCard className="h-3.5 w-3.5 text-primary/70" />
                          <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Meio de Pagamento</span>
                        </div>
                        <div className="space-y-1.5">
                          <div className="flex items-center justify-between">
                            <span className="text-[11px] text-muted-foreground">Nome</span>
                            <span className="text-xs font-medium text-foreground">{meio.nome}</span>
                          </div>
                          {meio.tipo_pagamento && (
                            <div className="flex items-center justify-between">
                              <span className="text-[11px] text-muted-foreground">Tipo</span>
                              <Badge variant="outline" className="text-[10px] font-medium flex items-center gap-1">
                                {(() => {
                                  const Icon = (PaymentMethodIcon as Record<string, React.ComponentType<{ className?: string }>>)[meio.tipo_pagamento] || DefaultPaymentIcon;
                                  return <Icon className="h-3 w-3" />;
                                })()}
                                {TIPOS_PAGAMENTO.find(t => t.value === meio.tipo_pagamento)?.label || meio.tipo_pagamento}
                              </Badge>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Coluna: Conta Corrente */}
                    {conta && (
                      <div className="bg-muted/30 border border-border/30 rounded-lg p-3 space-y-2">
                        <div className="flex items-center gap-2 mb-2">
                          <Landmark className="h-3.5 w-3.5 text-primary/70" />
                          <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Conta Corrente</span>
                        </div>
                        <div className="space-y-1.5">
                          <div className="flex items-center justify-between">
                            <span className="text-[11px] text-muted-foreground">Nome</span>
                            <span className="text-xs font-medium text-foreground">{conta.nome}</span>
                          </div>
                          {conta.banco_nome && (
                            <div className="flex items-center justify-between">
                              <span className="text-[11px] text-muted-foreground">Banco</span>
                              <span className="text-xs text-foreground">{conta.banco_nome}</span>
                            </div>
                          )}
                          {conta.tipo_conta && (
                            <div className="flex items-center justify-between">
                              <span className="text-[11px] text-muted-foreground">Tipo</span>
                              <span className="text-xs text-foreground capitalize">{conta.tipo_conta}</span>
                            </div>
                          )}
                          {conta.provider_slug && (
                            <div className="flex items-center justify-between">
                              <span className="text-[11px] text-muted-foreground">Provider</span>
                              <Badge variant="secondary" className="text-[10px] font-medium capitalize">
                                {conta.provider_slug}
                              </Badge>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                </section>

                {/* ===== Observações (somente se houver) ===== */}
                {assin.observacoes && (
                  <>
                    <Separator />
                    <section className="space-y-2">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-primary" />
                        <h3 className="text-sm font-semibold">Observações</h3>
                      </div>
                      <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">
                        {assin.observacoes}
                      </p>
                    </section>
                  </>
                )}

                {/* Veículos vinculados (módulo Veículos) */}
                <FinanceiroVehiclesBlock assinaturaId={assinaturaId} enabled={open} />

                <Separator />

                {/* v1.3.0: Juros/Multa + Expiração (readonly colapsáveis) */}
                {(() => {
                  const j = (assin as any).juros || {};
                  const jurosValue = {
                    enabled: Boolean(j?.enabled),
                    multa_tipo: (j?.multa_tipo as 'percent' | 'fixed') || 'percent',
                    multa_valor: Number(j?.multa_valor) || 0,
                    juros_tipo: (j?.juros_tipo as 'percent' | 'fixed') || 'percent',
                    juros_valor: Number(j?.juros_valor) || 0,
                  };
                  const dias = Number((assin as any).dias_expiracao_pos_vencimento) || 60;
                  return (
                    <div className="space-y-3">
                      <SubscriptionJurosSection value={jurosValue} readOnly defaultOpen={false} />
                      <SubscriptionExpiracaoSection value={dias} readOnly defaultOpen={false} />
                    </div>
                  );
                })()}

                <Separator />

                {/* ===== Composição (itens snapshot) — padrão Itens da Cobrança ===== */}
                <section>
                  <div className="flex items-center gap-2 mb-3">
                    <ShoppingCart className="h-4 w-4 text-primary" />
                    <h3 className="text-sm font-semibold text-foreground">Composição do ciclo</h3>
                  </div>

                  {itens.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-4 text-muted-foreground">
                      <Package className="h-5 w-5 mb-1.5 opacity-40" />
                      <span className="text-xs">Sem itens registrados</span>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {/* Cabeçalho — oculto no mobile */}
                      <div className="hidden sm:grid grid-cols-12 gap-2 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold px-2 pb-1 border-b border-border/30">
                        <span className="col-span-5">Produto</span>
                        <span className="col-span-1 text-center">Qtd</span>
                        <span className="col-span-2 text-right">Unitário</span>
                        <span className="col-span-2 text-right">Desconto</span>
                        <span className="col-span-2 text-right">Total</span>
                      </div>

                      {itens.map(it => {
                        const itemTotal = it.total ?? (it.quantidade * it.valor_unitario - (it.desconto || 0));
                        const label = it.product_name || it.descricao || '—';
                        return (
                          <div key={it.id} className="rounded-lg bg-muted/30 border border-border/20 text-xs">
                            {/* Desktop: grid horizontal */}
                            <div className="hidden sm:grid grid-cols-12 gap-2 items-center px-2 py-1.5">
                              <span className="col-span-5 font-medium text-foreground truncate" title={label}>
                                {label}
                                {it.product_name && it.descricao && it.descricao !== it.product_name && (
                                  <span className="ml-2 text-[10px] text-muted-foreground inline-flex items-center gap-1">
                                    <Tag className="h-3 w-3" />
                                    {it.descricao}
                                  </span>
                                )}
                              </span>
                              <span className="col-span-1 text-center text-muted-foreground">{it.quantidade}</span>
                              <span className="col-span-2 text-right text-foreground">{formatBRL(it.valor_unitario)}</span>
                              <span className="col-span-2 text-right">
                                {it.desconto && it.desconto > 0 ? (
                                  <span className="text-destructive">-{formatBRL(it.desconto)}</span>
                                ) : (
                                  <span className="text-muted-foreground/50">—</span>
                                )}
                              </span>
                              <span className="col-span-2 text-right font-semibold text-foreground">{formatBRL(itemTotal)}</span>
                            </div>
                            {/* Mobile: empilhado */}
                            <div className="sm:hidden px-2.5 py-2 space-y-1">
                              <div className="flex items-center justify-between">
                                <span className="font-medium text-foreground truncate flex-1 mr-2" title={label}>
                                  {label}
                                </span>
                                <span className="font-semibold text-foreground shrink-0">{formatBRL(itemTotal)}</span>
                              </div>
                              <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
                                <span>Qtd: {it.quantidade}</span>
                                <span>Unit: {formatBRL(it.valor_unitario)}</span>
                                {it.desconto && it.desconto > 0 && (
                                  <span className="text-destructive">Desc: -{formatBRL(it.desconto)}</span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}

                      {/* Rodapé com totais */}
                      {(() => {
                        const subtotal = itens.reduce((s, it) => s + it.quantidade * it.valor_unitario, 0);
                        const descontos = itens.reduce((s, it) => s + (it.desconto || 0), 0);
                        const total = itens.reduce(
                          (s, it) => s + (it.total ?? it.quantidade * it.valor_unitario - (it.desconto || 0)),
                          0,
                        );
                        return (
                          <div className="border-t border-border/40 pt-2 mt-1 space-y-1 px-2">
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-muted-foreground">Subtotal</span>
                              <span className="font-medium text-foreground">{formatBRL(subtotal)}</span>
                            </div>
                            {descontos > 0 && (
                              <div className="flex items-center justify-between text-xs">
                                <span className="text-muted-foreground">Descontos</span>
                                <span className="font-medium text-destructive">-{formatBRL(descontos)}</span>
                              </div>
                            )}
                            <div className="flex items-center justify-between text-sm font-bold pt-1 border-t border-border/30">
                              <span className="text-foreground">Total</span>
                              <span className="text-foreground">{formatBRL(total)}</span>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </section>

                <Separator />

                {/* ===== Ciclos — padrão Parcelas (sanfona + altura fixa ~6 itens) ===== */}
                <section>
                  <Collapsible open={ciclosOpen} onOpenChange={setCiclosOpen}>
                    <CollapsibleTrigger className="w-full flex items-center gap-2 mb-3 group focus:outline-none">
                      <Receipt className="h-4 w-4 text-primary" />
                      <h3 className="text-sm font-semibold text-foreground">Ciclos</h3>
                      <Badge variant="outline" className="text-[10px]">
                        {ciclos.length}
                      </Badge>
                      <ChevronDown
                        className={cn(
                          'h-4 w-4 ml-auto text-muted-foreground transition-transform',
                          ciclosOpen && 'rotate-180',
                        )}
                      />
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                  {ciclos.length === 0 ? (
                    <p className="text-xs text-muted-foreground text-center py-2">Nenhum ciclo gerado</p>
                  ) : (
                    <>
                      {/* Cabeçalho — oculto no mobile (fixo, fora da área rolável) */}
                      <div className="hidden sm:flex items-center gap-3 text-[10px] uppercase tracking-wider text-muted-foreground px-2.5 pb-1">
                        <span className="w-8">#</span>
                        <span className="w-24">N.Fatura</span>
                        <span className="w-24">Valor</span>
                        <span className="w-20 text-center">Vencimento</span>
                        <span className="w-20 text-center">Pagamento</span>
                        {/* v1.1.1 — coluna Meio Pgto. (alterável pela edição de recorrência) */}
                        <span className="w-32">Meio Pgto.</span>
                        <span className="flex-1 text-center">Status</span>
                        <span className="w-10 text-center">Link</span>
                        <span className="w-40 text-right">Ações</span>
                      </div>

                      {/* v1.7.0 — ScrollArea própria com altura fixa: a ScrollArea externa do modal
                          envolve os filhos em contêiner de tabela, o que anula max-h/overflow do
                          div simples e impedia rolar os ciclos além dos primeiros itens. */}
                      <div
                        className={cn(
                          'w-full overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent',
                          // v1.8.1 — altura fixa (não max-h, anulada pela área rolável externa) só para listas longas
                          ciclos.length > 8 && 'h-[27rem]',
                        )}
                      >
                        <div className="space-y-1.5 w-full">

                      {ciclos.map(c => {
                        const cb = cicloStatusBadge[c.status] || cicloStatusBadge.aguardando_emissao;
                        // Feature 2: requer permissão dedicada `financial_subscription_anticipate`
                        const podeAntecipar = c.status === 'aguardando_emissao' && !isCancelada && canAnticipate;
                        // Helper para abrir o diálogo com o contexto completo do ciclo
                        const abrirAntecipar = () => setCicloAntecipar({
                          cobranca_id: c.cobranca_id,
                          ciclo_numero: c.ciclo_numero,
                          valor_total: Number(c.valor_total || 0),
                          data_vencimento: c.data_vencimento,
                        });
                        // v1.6.0 — status aceitos pela RPC cancel_charge_manual.
                        // Pagos/estornados/cancelados e cobranças em processamento não podem ser excluídos.
                        const podeExcluir =
                          canDeleteCharge &&
                          ['aguardando_emissao', 'pendente', 'aguardando_pagamento', 'vencido', 'parcial'].includes(
                            c.status,
                          );
                        const abrirExcluir = () => {
                          setMotivoExclusao('');
                          setCicloExcluir({
                            cobranca_id: c.cobranca_id,
                            ciclo_numero: c.ciclo_numero,
                            valor_total: Number(c.valor_total || 0),
                            data_vencimento: c.data_vencimento,
                            tem_provider: !!c.provider_charge_id,
                          });
                        };
                        return (
                          <div key={c.cobranca_id} className="rounded-lg bg-muted/30 border border-border/30">
                            {/* Desktop */}
                            <div className="hidden sm:flex items-center gap-3 text-xs p-2.5">
                              {/* v1.2.0 — Área clicável: campos #/N.Fatura/Valor/Vencimento/Pagamento/Meio/Status abrem o modal de detalhes da cobrança */}
                              <button
                                type="button"
                                onClick={() => openCicloDetails(c.cobranca_id)}
                                title="Ver detalhes da cobrança"
                                className="flex items-center gap-3 flex-1 min-w-0 text-left rounded-md hover:bg-muted/60 transition-colors cursor-pointer -m-1 p-1 focus:outline-none focus:ring-1 focus:ring-primary/40"
                              >
                                <span className="font-semibold text-foreground w-8">#{c.ciclo_numero}</span>
                                <span className="text-muted-foreground w-24 text-[10px] truncate font-mono" title={c.numero_fatura || ''}>
                                  {c.numero_fatura || '—'}
                                </span>
                                <span className="font-medium text-foreground w-24 font-mono">{formatBRL(c.valor_total)}</span>
                                <span className="text-muted-foreground w-20 text-center">
                                  {c.data_vencimento
                                    ? format(parseDateLocal(c.data_vencimento), 'dd/MM/yy', { locale: ptBR })
                                    : '—'}
                                </span>
                                <span className="w-20 text-center">
                                  {c.data_pagamento ? (
                                    <span className="text-green-600 dark:text-green-400 font-medium">
                                      {format(parseDateLocal(c.data_pagamento), 'dd/MM/yy', { locale: ptBR })}
                                    </span>
                                  ) : (
                                    <span className="text-muted-foreground/40">—</span>
                                  )}
                                </span>
                                {/* v1.1.1 — Meio de pagamento do ciclo (nome + badge de tipo) */}
                                <span className="w-32 flex items-center gap-1 min-w-0">
                                  {c.meio_pagamento_nome ? (
                                    <>
                                      <span
                                        className="text-foreground text-[11px] font-medium truncate"
                                        title={c.meio_pagamento_nome}
                                      >
                                        {c.meio_pagamento_nome}
                                      </span>
                                      {c.meio_pagamento_tipo && (
                                        <Badge
                                          variant="outline"
                                          className="text-[9px] px-1 py-0 leading-none shrink-0"
                                        >
                                          {TIPOS_PAGAMENTO.find(t => t.value === c.meio_pagamento_tipo)?.label ||
                                            c.meio_pagamento_tipo}
                                        </Badge>
                                      )}
                                    </>
                                  ) : (
                                    <span className="text-muted-foreground/40">—</span>
                                  )}
                                </span>
                                <span className="flex-1 flex justify-center">
                                  <span className={cn(
                                    'inline-flex items-center justify-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap',
                                    cb.className,
                                  )}>
                                    {cb.label}
                                  </span>
                                </span>
                              </button>
                              <span className="w-10 flex justify-center">
                                {/* v1.3.0 — resolveCheckoutUrl respeita checkout_mode da conta corrente
                                    (transparent → /c/:slug ; provider → invoiceUrl), com fallback automático. */}
                                {(() => {
                                  const resolvedCiclo = resolveCheckoutUrl({
                                    checkout_mode: checkoutMode,
                                    checkout_session_slug: c.checkout_session_slug,
                                    provider_invoice_url: c.provider_invoice_url,
                                    provider_payment_link: c.provider_payment_link,
                                  });
                                  return resolvedCiclo.url ? (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <a
                                          href={resolvedCiclo.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-primary hover:text-primary/80 transition-colors"
                                        >
                                          <ExternalLink className="h-3.5 w-3.5" />
                                        </a>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        {resolvedCiclo.origin === 'transparent'
                                          ? 'Abrir link do checkout transparente'
                                          : 'Abrir link de pagamento'}
                                      </TooltipContent>
                                    </Tooltip>
                                  ) : (
                                    <span className="text-muted-foreground/20"><ExternalLink className="h-3.5 w-3.5" /></span>
                                  );
                                })()}
                              </span>
                              <span className="w-40 flex justify-end items-center gap-1">
                                {/* Ciclo aguardando emissão → botão Antecipar (promove no provider) */}
                                {podeAntecipar && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <button
                                        type="button"
                                        onClick={abrirAntecipar}
                                        disabled={isBusy}
                                        className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-[11px] font-medium text-primary bg-primary/10 hover:bg-primary/20 transition-colors disabled:opacity-50 disabled:pointer-events-none"
                                      >
                                        {anteciparCiclo.isPending ? (
                                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                        ) : (
                                          <Rocket className="h-3.5 w-3.5" />
                                        )}
                                        Antecipar
                                      </button>
                                    </TooltipTrigger>
                                    <TooltipContent>Promove o ciclo e emite no provider agora</TooltipContent>
                                  </Tooltip>
                                )}
                                {/* Ciclo já emitido no provider → ações reutilizadas do ChargeDetailsModal */}
                                {!podeAntecipar && c.provider_charge_id && c.conta_corrente_id && (
                                  <ChargeProviderActions
                                    providerChargeId={c.provider_charge_id}
                                    contaCorrenteId={c.conta_corrente_id}
                                    providerInvoiceUrl={c.provider_invoice_url}
                                    providerPaymentLink={c.provider_payment_link}
                                    providerSlug={c.provider_slug}
                                    status={c.status as any}
                                    billingType={c.billing_type}
                                    tipoCobranca={c.tipo_cobranca}
                                    receiptUrl={c.receipt_url}
                                  />
                                )}
                                {/* v1.6.0 — Excluir ciclo (cancela a cobrança gerada; sincroniza no provider) */}
                                {podeExcluir && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <button
                                        type="button"
                                        onClick={abrirExcluir}
                                        disabled={cancelChargeManual.isPending}
                                        aria-label="Excluir ciclo"
                                        className="inline-flex items-center justify-center h-7 w-7 rounded-md text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50 disabled:pointer-events-none"
                                      >
                                        <Trash2 className="h-3.5 w-3.5" />
                                      </button>
                                    </TooltipTrigger>
                                    <TooltipContent>Excluir este ciclo (cancela a cobrança)</TooltipContent>
                                  </Tooltip>
                                )}
                              </span>
                            </div>

                            {/* Mobile: mini-card empilhado */}
                            <div className="sm:hidden px-2.5 py-2 space-y-1.5 text-xs">
                              {/* v1.2.0 — bloco de info clicável para abrir detalhes da cobrança */}
                              <button
                                type="button"
                                onClick={() => openCicloDetails(c.cobranca_id)}
                                className="w-full text-left space-y-1.5 rounded-md hover:bg-muted/60 transition-colors -mx-1 px-1 py-1 cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary/40"
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <span className="font-semibold text-foreground">#{c.ciclo_numero}</span>
                                    <span className={cn(
                                      'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium',
                                      cb.className,
                                    )}>
                                      {cb.label}
                                    </span>
                                  </div>
                                  <span className="font-semibold text-foreground font-mono">{formatBRL(c.valor_total)}</span>
                                </div>
                                <div className="flex items-center gap-3 text-[10px] text-muted-foreground flex-wrap">
                                  <span>
                                    Venc:{' '}
                                    {c.data_vencimento
                                      ? format(parseDateLocal(c.data_vencimento), 'dd/MM/yy', { locale: ptBR })
                                      : '—'}
                                  </span>
                                  {c.data_pagamento && (
                                    <span className="text-green-600 dark:text-green-400 font-medium">
                                      Pago: {format(parseDateLocal(c.data_pagamento), 'dd/MM/yy', { locale: ptBR })}
                                    </span>
                                  )}
                                  {c.numero_fatura && (
                                    <span className="truncate">Fatura: {c.numero_fatura}</span>
                                  )}
                                  {/* v1.1.1 — Meio de pagamento do ciclo no card mobile */}
                                  {c.meio_pagamento_nome && (
                                    <span className="truncate">
                                      Meio: {c.meio_pagamento_nome}
                                      {c.meio_pagamento_tipo
                                        ? ` (${TIPOS_PAGAMENTO.find(t => t.value === c.meio_pagamento_tipo)?.label || c.meio_pagamento_tipo})`
                                        : ''}
                                    </span>
                                  )}
                                </div>
                              </button>
                              <div className="flex items-center gap-3 pt-0.5">
                                {/* v1.3.0 — mesma regra do desktop: resolveCheckoutUrl honra checkout_mode. */}
                                {(() => {
                                  const resolvedCicloMobile = resolveCheckoutUrl({
                                    checkout_mode: checkoutMode,
                                    checkout_session_slug: c.checkout_session_slug,
                                    provider_invoice_url: c.provider_invoice_url,
                                    provider_payment_link: c.provider_payment_link,
                                  });
                                  return resolvedCicloMobile.url ? (
                                    <a
                                      href={resolvedCicloMobile.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-primary hover:text-primary/80 transition-colors flex items-center gap-1 text-[10px]"
                                    >
                                      <ExternalLink className="h-3 w-3" /> Link
                                    </a>
                                  ) : null;
                                })()}
                                {podeAntecipar && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-6 gap-1 text-[10px] px-2"
                                    onClick={abrirAntecipar}
                                    disabled={isBusy}
                                  >
                                    {anteciparCiclo.isPending ? (
                                      <Loader2 className="h-3 w-3 animate-spin" />
                                    ) : (
                                      <Zap className="h-3 w-3" />
                                    )}
                                    Antecipar
                                  </Button>
                                )}
                                {/* Atalhos do provider (PIX/Boleto/Link) também no mobile */}
                                {!podeAntecipar && c.provider_charge_id && c.conta_corrente_id && (
                                  <ChargeProviderActions
                                    providerChargeId={c.provider_charge_id}
                                    contaCorrenteId={c.conta_corrente_id}
                                    providerInvoiceUrl={c.provider_invoice_url}
                                    providerPaymentLink={c.provider_payment_link}
                                    providerSlug={c.provider_slug}
                                    status={c.status as any}
                                    billingType={c.billing_type}
                                    tipoCobranca={c.tipo_cobranca}
                                    receiptUrl={c.receipt_url}
                                  />
                                )}
                                {/* v1.6.0 — Excluir ciclo também no card mobile */}
                                {podeExcluir && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 gap-1 text-[10px] px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                                    onClick={abrirExcluir}
                                    disabled={cancelChargeManual.isPending}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                    Excluir
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                        </div>
                      </div>
                    </>
                  )}
                    </CollapsibleContent>
                  </Collapsible>
                </section>

                <Separator />

                {/* ===== Histórico da recorrência (timeline, sanfona) =====
                    Reaproveita o componente ChargeTimeline (mesmo da Jornada
                    da cobrança). Os eventos vêm da RPC get_assinatura_details
                    e incluem ações manuais, eventos de sistema e webhooks
                    do provider em qualquer ciclo filho. */}
                <section>
                  <Collapsible open={historicoOpen} onOpenChange={setHistoricoOpen}>
                    <CollapsibleTrigger className="w-full flex items-center gap-2 mb-3 group focus:outline-none">
                      <History className="h-4 w-4 text-primary" />
                      <h3 className="text-sm font-semibold text-foreground">
                        Histórico da recorrência
                      </h3>
                      <Badge variant="outline" className="text-[10px]">
                        {data?.timeline?.length ?? 0}
                      </Badge>
                      <ChevronDown
                        className={cn(
                          'h-4 w-4 ml-auto text-muted-foreground transition-transform',
                          historicoOpen && 'rotate-180',
                        )}
                      />
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <ChargeTimeline
                        timelineEvents={data?.timeline || []}
                        createdAt={assin.created_at}
                        isLoading={isLoading}
                        skipVirtualEvents
                        lookups={timelineLookups}
                      />
                    </CollapsibleContent>
                  </Collapsible>
                </section>
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* ===== Confirmação de cancelamento ===== */}
      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Cancelar recorrência?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Todos os ciclos <strong>futuros</strong> que ainda estão pendentes serão
              cancelados. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="flex items-start gap-2 rounded-md border p-3 bg-muted/40">
            <Checkbox
              id="cancelar-provider"
              checked={cancelarNoProvider}
              onCheckedChange={v => setCancelarNoProvider(!!v)}
            />
            <div className="space-y-1">
              <label
                htmlFor="cancelar-provider"
                className="text-sm font-medium leading-none cursor-pointer"
              >
                Cancelar também ciclos já emitidos no provider
              </label>
              <p className="text-xs text-muted-foreground">
                Marque para enviar requisição de cancelamento ao gateway (Asaas) para
                cobranças já criadas e ainda não pagas.
              </p>
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={isBusy}>Voltar</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                await cancelar.mutateAsync(cancelarNoProvider);
                setCancelOpen(false);
                setCancelarNoProvider(false);
              }}
              disabled={isBusy}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {cancelar.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}
              Cancelar recorrência
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal de Edição da Recorrência (zonas livre/sensível/bloqueada) */}
      <EditSubscriptionModal
        assinaturaId={assinaturaId}
        open={editOpen}
        onOpenChange={setEditOpen}
      />

      {/* v1.2.0 — Modal aninhado: detalhes da cobrança/fatura ao clicar em um ciclo */}
      <ChargeDetailsModal
        cobranca={selectedCicloCobrancaId ? ({ id: selectedCicloCobrancaId } as FinanceiroCobranca) : null}
        open={chargeDetailsOpen}
        onOpenChange={(v) => {
          setChargeDetailsOpen(v);
          if (!v) {
            setSelectedCicloCobrancaId(null);
            // v1.2.1 — Ao fechar o modal de detalhes da cobrança, força
            // refetch dos detalhes da recorrência. Cobre alterações
            // realizadas no ciclo (reenvio ao provider, edição, cancelamento)
            // que podem não ter chegado pelo realtime a tempo.
            if (assinaturaId) {
              queryClient.invalidateQueries({ queryKey: ['assinatura-detalhes', assinaturaId] });
            }
          }
        }}
      />

      {/* Feature 2 — Diálogo reforçado de antecipação (digite "ANTECIPAR" para confirmar) */}
      <AnticipateConfirmDialog
        open={!!cicloAntecipar}
        onOpenChange={(v) => { if (!v) setCicloAntecipar(null); }}
        isPending={anteciparCiclo.isPending}
        customerName={customer?.name ?? null}
        valor={cicloAntecipar?.valor_total ?? 0}
        moeda={assin?.moeda ?? 'BRL'}
        dataVencimentoOriginal={cicloAntecipar?.data_vencimento ?? null}
        cicloNumero={cicloAntecipar?.ciclo_numero ?? null}
        // cardInfo será preenchido em migration futura (get_assinatura_details v1.9.0 com card_snapshot do default).
        cardInfo={null}
        onConfirm={() => {
          if (!cicloAntecipar) return;
          // Calcula dias antecipados local — mesmo cálculo do diálogo
          const ymd = (cicloAntecipar.data_vencimento || '').substring(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
          let dias = 0;
          if (ymd) {
            const [, y, m, d] = ymd;
            const venc = new Date(Number(y), Number(m) - 1, Number(d));
            const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
            dias = Math.max(0, Math.round((venc.getTime() - hoje.getTime()) / 86400000));
          }
          anteciparCiclo.mutate(
            {
              cobrancaId: cicloAntecipar.cobranca_id,
              auditPayload: {
                valor: cicloAntecipar.valor_total,
                dias_antecipados: dias,
                step_up_method: 'typed_keyword:ANTECIPAR',
              },
            },
            { onSettled: () => setCicloAntecipar(null) },
          );
        }}
      />

      {/* ===== v1.6.0 — Confirmação de exclusão de um ciclo (cancela a cobrança gerada) =====
          Reaproveita a RPC validada `cancel_charge_manual`: cancela cobrança + parcelas
          de forma atômica e, quando a cobrança já foi emitida no provider, enfileira o
          cancelamento no gateway. Ciclos pagos nunca chegam aqui (botão oculto). */}
      <AlertDialog
        open={!!cicloExcluir}
        onOpenChange={(v) => { if (!v && !cancelChargeManual.isPending) setCicloExcluir(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir ciclo #{cicloExcluir?.ciclo_numero}?</AlertDialogTitle>
            <AlertDialogDescription>
              A cobrança deste ciclo será cancelada
              {cicloExcluir?.data_vencimento
                ? ` (vencimento ${format(parseDateLocal(cicloExcluir.data_vencimento), 'dd/MM/yyyy', { locale: ptBR })})`
                : ''}
              {' '}no valor de {formatBRL(cicloExcluir?.valor_total ?? 0, assin?.moeda)}.
              {cicloExcluir?.tem_provider
                ? ' Como ela já foi emitida, o cancelamento também será enviado ao provedor de pagamento.'
                : ' Ela ainda não foi emitida no provedor.'}
              {' '}Esta ação não pode ser desfeita e o ciclo não será regerado.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-2 py-1">
            <Label htmlFor="motivo-exclusao-ciclo" className="text-xs">
              Motivo (opcional)
            </Label>
            <Textarea
              id="motivo-exclusao-ciclo"
              value={motivoExclusao}
              onChange={(e) => setMotivoExclusao(e.target.value.slice(0, 500))}
              maxLength={500}
              rows={3}
              placeholder="Ex.: ciclo gerado por engano na edição da recorrência"
              className="text-sm"
            />
          </div>

          <InlineNotice variant="warning">
            Se esta cobrança contiver o item do plano principal, o sistema recusará a exclusão —
            nesse caso use o cancelamento da recorrência.
          </InlineNotice>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelChargeManual.isPending}>Voltar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={cancelChargeManual.isPending}
              onClick={async (e) => {
                e.preventDefault();
                if (!cicloExcluir) return;
                try {
                  await cancelChargeManual.mutateAsync({
                    cobranca_id: cicloExcluir.cobranca_id,
                    reason: motivoExclusao.trim() || null,
                  });
                  // Recarrega os detalhes da recorrência para a linha refletir o novo status
                  if (assinaturaId) {
                    queryClient.invalidateQueries({ queryKey: ['assinatura-detalhes', assinaturaId] });
                  }
                  queryClient.invalidateQueries({ queryKey: ['financeiro-assinaturas'] });
                  setCicloExcluir(null);
                } catch {
                  // Toast de erro já é exibido pelo hook (mensagem da RPC)
                }
              }}
            >
              {cancelChargeManual.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}
              Excluir ciclo
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>



      {/* v1.4.0 — Modal de detalhes do cliente acionado pelo atalho na seção Pagador */}
      <CustomerDetailsModalPremium
        customerId={assin?.customer_id ?? null}
        open={customerModalOpen}
        onOpenChange={setCustomerModalOpen}
      />
    </>

  );
}

// ============================================================================
// Fase 5/C.2 — Componente local: seção "Cartão padrão" da assinatura
// ----------------------------------------------------------------------------
// Exibe o cartão atualmente em uso como default_card_token_id (quando houver)
// e permite ao operador trocar/remover via AlertDialog usando SavedCardsList.
// ============================================================================
interface SubscriptionDefaultCardSectionProps {
  assinaturaId: string;
  customerId: string;
  defaultCardTokenId: string | null;
  cardDunningState: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pickedCardId: string | null;
  setPickedCardId: (id: string | null) => void;
  atualizar: ReturnType<typeof useAssinaturaActions>['atualizar'];
}

function SubscriptionDefaultCardSection({
  assinaturaId,
  customerId,
  defaultCardTokenId,
  cardDunningState,
  open,
  onOpenChange,
  pickedCardId,
  setPickedCardId,
  atualizar,
}: SubscriptionDefaultCardSectionProps) {
  const { data: cards } = useSavedCards(customerId, true);
  const currentCard = cards?.find(c => c.id === defaultCardTokenId);
  const isFailed = cardDunningState === 'failed';

  const handleSave = () => {
    atualizar.mutate(
      { default_card_token_id: pickedCardId } as any,
      {
        onSuccess: () => {
          onOpenChange(false);
        },
      },
    );
  };

  return (
    <>
      <div className="bg-muted/30 border border-border/30 rounded-lg p-3 mt-3">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="flex items-center gap-2">
            <CreditCard className="h-3.5 w-3.5 text-primary/70" />
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
              Cartão padrão
            </span>
            {isFailed && (
              <Badge variant="destructive" className="text-[10px]">
                Cartão com falhas — atualizar
              </Badge>
            )}
          </div>
          <Button
            size="xs"
            variant="outline"
            onClick={() => {
              setPickedCardId(defaultCardTokenId);
              onOpenChange(true);
            }}
          >
            Trocar cartão
          </Button>
        </div>
        {currentCard ? (
          <div className="text-xs text-foreground">
            {currentCard.brand?.toUpperCase() || 'CARTÃO'} •••• {currentCard.last4}
            <span className="text-muted-foreground ml-2">
              · {String(currentCard.exp_month).padStart(2, '0')}/{currentCard.exp_year}
            </span>
          </div>
        ) : (
          <div className="text-xs text-muted-foreground">
            Sem cartão padrão — link de checkout será enviado em cada ciclo.
          </div>
        )}
      </div>

      <AlertDialog open={open} onOpenChange={onOpenChange}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>Trocar cartão padrão</AlertDialogTitle>
            <AlertDialogDescription>
              Escolha um cartão salvo do cliente. O novo cartão passará a ser usado nos próximos ciclos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="max-h-[420px] overflow-y-auto py-2">
            <SavedCardsList
              customerId={customerId}
              selectable
              canRevoke={false}
              selectedCardId={pickedCardId}
              onSelect={setPickedCardId}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={atualizar.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); handleSave(); }} disabled={atualizar.isPending}>
              {atualizar.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Salvar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default SubscriptionDetailsModal;
